/**
 * LayeredAvatarLab — the Character Customizer, and the app's avatar picker.
 * Two modes: "Custom" builds a layered look; "Ready Avatars" picks a pre-made avatar.
 * Save persists the choice (custom config OR ready id) as the user's avatar everywhere.
 * Economy UI is present but free for now (no prices, no locks).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, useWindowDimensions, View } from 'react-native';
import { FlashList, type ListRenderItemInfo } from '@shopify/flash-list';
import { Dices, X } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../../src/theme/useTheme';
import { useAvatarContext } from '../../../src/avatars/contexts/AvatarContext';
import { DEFAULT_AVATAR_ID, type AvatarId } from '../../../constants/avatarIds';
import { CATALOG, CATS } from './characterCustomizer.constants';
import {
  collectCategoryItemUrls,
  collectEquippedLayers,
  columnsForWidth,
  lockedCategoriesFor,
} from './characterCustomizer.helper';
import { prefetchCustomizerAssets } from './characterCustomizer.assets';
import { useWardrobe } from './useWardrobe';
import { CharacterStage } from './CharacterStage';
import { StadiumBackdrop } from './StadiumBackdrop';
import { CategoryRail } from './CategoryRail';
import { ShopCell } from './ShopCell';
import { CoinPill } from './CoinPill';
import { CollectionBadge } from './CollectionBadge';
import { Sparkles } from './Sparkles';
import { CustomizerToast, type ToastTone } from './CustomizerToast';
import { PickerModeToggle, type PickerMode } from './PickerModeToggle';
import { AvatarDisplay } from '../AvatarDisplay/AvatarDisplay';
import { AvatarPicker } from '../AvatarPicker/AvatarPicker';
import { getLayeredAvatarLabStyles, getStageHeaderPadStyle } from './LayeredAvatarLab.styles';
import type { CustomizerCategoryId, CustomizerItem } from './characterCustomizer.types';

interface LayeredAvatarLabProps {
  onClose: () => void;
}

interface ToastState {
  id: number;
  message: string;
  tone: ToastTone;
}

const STAGE_CHARACTER_SIZE = 214;

export const LayeredAvatarLab: React.FC<LayeredAvatarLabProps> = ({ onClose }) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const gridColumns = useMemo(() => columnsForWidth(width), [width]);
  const styles = useMemo(() => getLayeredAvatarLabStyles(theme), [theme]);

  const {
    currentAvatarId,
    currentAvatarConfig,
    isLoadingAvatar,
    updateAvatar,
    updateAvatarConfig,
  } = useAvatarContext();

  // Seed the builder from the user's saved custom look (merged over defaults).
  const { equipped, equip, randomize, coins } = useWardrobe(currentAvatarConfig ?? undefined);
  // Always open on the layered ("Custom") builder; the Ready tab is one tap away.
  const [mode, setMode] = useState<PickerMode>('custom');
  const [selectedReadyId, setSelectedReadyId] = useState<AvatarId>(
    currentAvatarId ?? DEFAULT_AVATAR_ID
  );
  const [activeCategory, setActiveCategory] = useState<CustomizerCategoryId>('hat');
  const [burst, setBurst] = useState(0);
  const [toast, setToast] = useState<ToastState | null>(null);

  const fireBurst = useCallback(() => setBurst((b) => b + 1), []);
  const fireToast = useCallback((message: string, tone: ToastTone) => {
    setToast({ id: Date.now(), message, tone });
  }, []);
  const clearToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    prefetchCustomizerAssets(collectEquippedLayers(equipped).map((l) => l.url));
  }, [equipped]);
  useEffect(() => {
    prefetchCustomizerAssets(collectCategoryItemUrls(activeCategory));
  }, [activeCategory]);

  const handleSave = useCallback(() => {
    if (mode === 'ready') {
      void updateAvatar(selectedReadyId, onClose);
    } else {
      void updateAvatarConfig(equipped, onClose);
    }
  }, [mode, updateAvatar, updateAvatarConfig, selectedReadyId, equipped, onClose]);

  const handleSurprise = useCallback(() => {
    randomize();
    fireToast('Surprise fit! 🎲', 'win');
    fireBurst();
  }, [fireBurst, fireToast, randomize]);

  // Women bases lock the Hair tab (shown greyed + lock icon) — they ship with a
  // built-in hairstyle. Tapping the locked tab explains why instead of switching.
  const lockedCats = useMemo<ReadonlySet<CustomizerCategoryId>>(
    () => lockedCategoriesFor(equipped),
    [equipped]
  );

  // Never strand the user on a locked tab's grid: if the active tab becomes locked
  // (e.g. switching to a women base while on Hair), fall back to the first unlocked
  // category ('base' is never locked, so this always resolves).
  useEffect(() => {
    if (lockedCats.has(activeCategory)) {
      setActiveCategory(CATS.find((c) => !lockedCats.has(c)) ?? 'base');
    }
  }, [lockedCats, activeCategory]);

  const handleLockedCategory = useCallback(() => {
    fireToast('These avatars come with their own hairstyle 💇‍♀️', 'info');
  }, [fireToast]);

  // "Dressed" progress: how many equip slots are filled (locked categories don't count).
  // Hair & hat are mutually exclusive (equipping one drops the other), so they can never
  // both be worn — count the pair as a single reachable slot. Otherwise the badge would
  // show a "/ 7" that's impossible to complete. On women bases hair is already locked
  // (total drops to 6 via `slots`), so only subtract when both are actually available.
  const dressed = useMemo(() => {
    const slots = CATS.filter((c) => !lockedCats.has(c));
    const have = slots.filter((c) => equipped[c] != null).length;
    const exclusivePairAvailable = !lockedCats.has('hair') && !lockedCats.has('hat');
    return { have, total: slots.length - (exclusivePairAvailable ? 1 : 0) };
  }, [equipped, lockedCats]);

  const definition = CATALOG[activeCategory];
  const gridData = useMemo<(CustomizerItem | null)[]>(
    () => (definition.removable ? [null, ...definition.items] : definition.items),
    [definition]
  );

  const renderCell = useCallback(
    ({ item }: ListRenderItemInfo<CustomizerItem | null>) => {
      const active = equipped[activeCategory] === (item ? item.id : null);
      return (
        <View style={styles.gridCell}>
          <ShopCell
            category={activeCategory}
            item={item}
            active={active}
            onPress={() => {
              equip(activeCategory, item ? item.id : null);
              fireBurst();
            }}
          />
        </View>
      );
    },
    [activeCategory, equip, equipped, fireBurst, styles.gridCell]
  );

  const isCustom = mode === 'custom';

  return (
    <View style={styles.root}>
      <View style={[styles.header, getStageHeaderPadStyle(insets.top)]}>
        <View style={styles.headerRow}>
          <Pressable
            style={styles.iconButton}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close customizer"
          >
            <X size={20} color={theme.white} />
          </Pressable>
          <CoinPill coins={coins} />
          <Pressable
            style={styles.saveButton}
            onPress={handleSave}
            disabled={isLoadingAvatar}
            accessibilityRole="button"
            accessibilityLabel="Save look"
          >
            <Text style={styles.saveButtonText}>Save</Text>
          </Pressable>
        </View>
        <View style={styles.toggleRow}>
          <View style={styles.toggleSide} />
          <PickerModeToggle mode={mode} onChange={setMode} />
          <View style={styles.toggleSide} />
        </View>
      </View>

      <View style={styles.stage}>
        <StadiumBackdrop />
        {isCustom ? (
          <View style={styles.badgeOnStage} pointerEvents="none">
            <CollectionBadge have={dressed.have} total={dressed.total} />
          </View>
        ) : null}
        <View style={styles.stageCharacter}>
          {isCustom ? (
            <>
              <CharacterStage equipped={equipped} size={STAGE_CHARACTER_SIZE} onPoke={fireBurst} />
              {burst > 0 ? <Sparkles key={burst} /> : null}
            </>
          ) : (
            <AvatarDisplay avatarId={selectedReadyId} size={STAGE_CHARACTER_SIZE} rounded={false} />
          )}
        </View>
      </View>

      {isCustom ? (
        <View style={styles.shop}>
          <CategoryRail
            categories={CATS}
            activeCategory={activeCategory}
            lockedCategories={lockedCats}
            onSelect={setActiveCategory}
            onLockedPress={handleLockedCategory}
          />
          <View style={styles.shopBody}>
            <View style={styles.shopHeader}>
              <Text style={styles.shopTitle}>{definition.label}</Text>
              <Pressable
                style={styles.surpriseButton}
                onPress={handleSurprise}
                accessibilityRole="button"
                accessibilityLabel="Surprise me with a random look"
              >
                <Dices size={16} color={theme.primaryLight} />
                <Text style={styles.surpriseText}>Surprise me</Text>
              </Pressable>
            </View>
            <FlashList<CustomizerItem | null>
              key={`${activeCategory}-${gridColumns}`}
              data={gridData}
              numColumns={gridColumns}
              renderItem={renderCell}
              keyExtractor={(item, index) => (item ? item.id : `none-${index}`)}
              contentContainerStyle={styles.gridContent}
              extraData={equipped[activeCategory]}
            />
          </View>
        </View>
      ) : (
        <ScrollView style={styles.readyScroll} contentContainerStyle={styles.readyContent}>
          <AvatarPicker selectedAvatarId={selectedReadyId} onAvatarSelect={setSelectedReadyId} />
        </ScrollView>
      )}

      {toast ? (
        <CustomizerToast key={toast.id} message={toast.message} tone={toast.tone} onDone={clearToast} />
      ) : null}
    </View>
  );
};
